/**
 * Pricebook Entry Modal
 *
 * Add/Edit product in pricebook modal
 */

import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAddPricebookEntry, useUpdatePricebookEntry } from '../../services/queries/pricebookQueries';
import { useProducts } from '../../services/queries/productQueries';
import { useToast } from '../../contexts/ToastContext';
import { X } from 'lucide-react';
import type { PricebookEntry, PriceTier } from '../../types/index';

interface PricebookEntryModalProps {
  isOpen: boolean;
  onClose: () => void;
  pricebookId: string;
  entry?: PricebookEntry | null;
}

export const PricebookEntryModal: React.FC<PricebookEntryModalProps> = ({
  isOpen,
  onClose,
  pricebookId,
  entry
}) => {
  const { success, error: showError } = useToast();
  const isEditMode = !!entry;

  const addEntry = useAddPricebookEntry();
  const updateEntry = useUpdatePricebookEntry();
  const { data: products } = useProducts();

  const [formData, setFormData] = useState<{
    productId: string;
    unitPrice: string;
    currency: string;
    isActive: boolean;
    effectiveDate: string;
    expirationDate: string;
    priceType: 'one-time' | 'recurring';
    recurringInterval: 'day' | 'week' | 'month' | 'year';
    recurringIntervalCount: string;
    pricingModel: 'per_unit' | 'tiered';
    tiersMode: 'graduated' | 'volume';
  }>({
    productId: '',
    unitPrice: '',
    currency: 'USD',
    isActive: true,
    effectiveDate: '',
    expirationDate: '',
    priceType: 'one-time',
    recurringInterval: 'month',
    recurringIntervalCount: '1',
    pricingModel: 'per_unit',
    tiersMode: 'graduated'
  });

  type TierRow = { upTo: string; unitAmount: string; flatAmount: string };
  const [tierRows, setTierRows] = useState<TierRow[]>([{ upTo: '', unitAmount: '', flatAmount: '' }]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (entry) {
      const hasRecurring = entry.recurring != null;
      const isTiered = entry.billingScheme === 'tiered';
      setFormData({
        productId: entry.productId,
        unitPrice: entry.unitPrice.toString(),
        currency: entry.currency,
        isActive: entry.isActive,
        effectiveDate: entry.effectiveDate || '',
        expirationDate: entry.expirationDate || '',
        priceType: hasRecurring ? 'recurring' : 'one-time',
        recurringInterval: hasRecurring && entry.recurring ? entry.recurring.interval : 'month',
        recurringIntervalCount: hasRecurring && entry.recurring?.intervalCount != null ? String(entry.recurring.intervalCount) : '1',
        pricingModel: isTiered ? 'tiered' : 'per_unit',
        tiersMode: isTiered && entry.tiersMode ? entry.tiersMode : 'graduated'
      });
      if (isTiered && entry.tiers && entry.tiers.length > 0) {
        setTierRows(
          entry.tiers.map((t) => ({
            upTo: t.upTo === 'inf' ? 'inf' : String(t.upTo),
            unitAmount: t.unitAmount != null ? (t.unitAmount / 100).toFixed(2) : '',
            flatAmount: t.flatAmount != null ? (t.flatAmount / 100).toFixed(2) : ''
          }))
        );
      } else {
        setTierRows([{ upTo: '', unitAmount: '', flatAmount: '' }]);
      }
    } else {
      setFormData({
        productId: '',
        unitPrice: '',
        currency: 'USD',
        isActive: true,
        effectiveDate: '',
        expirationDate: '',
        priceType: 'one-time',
        recurringInterval: 'month',
        recurringIntervalCount: '1',
        pricingModel: 'per_unit',
        tiersMode: 'graduated'
      });
      setTierRows([{ upTo: '', unitAmount: '', flatAmount: '' }]);
    }
    setErrors({});
  }, [entry, isOpen]);

  // Auto-fill price when product is selected
  useEffect(() => {
    if (formData.productId && !isEditMode && products) {
      const product = products.find(p => p.id === formData.productId);
      if (product) {
        setFormData(prev => ({
          ...prev,
          unitPrice: product.unitPrice.toString(),
          currency: product.currency
        }));
      }
    }
  }, [formData.productId, products, isEditMode]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const updateTierRow = (index: number, field: keyof TierRow, value: string) => {
    setTierRows(prev => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
    const key = `tier_${index}`;
    if (errors[key]) setErrors(prev => ({ ...prev, [key]: '' }));
  };

  const addTierRow = () => {
    setTierRows(prev => [...prev, { upTo: '', unitAmount: '', flatAmount: '' }]);
  };

  const removeTierRow = (index: number) => {
    if (tierRows.length <= 1) return;
    setTierRows(prev => prev.filter((_, i) => i !== index));
    setErrors(prev => {
      const next = { ...prev };
      delete next[`tier_${index}`];
      return next;
    });
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.productId) {
      newErrors.productId = 'Product is required';
    }
    if (formData.pricingModel === 'per_unit') {
      if (!formData.unitPrice || isNaN(Number(formData.unitPrice)) || Number(formData.unitPrice) < 0) {
        newErrors.unitPrice = 'Valid unit price is required';
      }
    } else {
      if (!tierRows.length || tierRows.every(r => !r.upTo && !r.unitAmount && !r.flatAmount)) {
        newErrors.tiers = 'At least one tier is required';
      }
      tierRows.forEach((row, i) => {
        const upToVal = row.upTo.trim().toLowerCase();
        const isInf = upToVal === 'inf' || upToVal === 'unlimited';
        const upToNum = isInf ? null : parseInt(row.upTo, 10);
        if (!isInf && (row.upTo === '' || isNaN(upToNum!) || upToNum! < 0)) {
          newErrors[`tier_${i}`] = 'Up to must be a number or "Unlimited"';
        }
        const unitNum = row.unitAmount === '' ? null : Number(row.unitAmount);
        const flatNum = row.flatAmount === '' ? null : Number(row.flatAmount);
        if ((unitNum == null || isNaN(unitNum) || unitNum < 0) && (flatNum == null || isNaN(flatNum) || flatNum < 0)) {
          if (!newErrors[`tier_${i}`]) newErrors[`tier_${i}`] = 'Unit amount or flat amount required';
        }
      });
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) {
      showError('Validation Error', 'Please fix the errors before submitting');
      return;
    }

    const intervalCountNum = formData.priceType === 'recurring' ? parseInt(formData.recurringIntervalCount, 10) : undefined;
    const isTiered = formData.pricingModel === 'tiered';
    const tiersPayload: PriceTier[] | undefined = isTiered
      ? tierRows
          .filter(r => r.upTo !== '' || r.unitAmount !== '' || r.flatAmount !== '')
          .map(r => {
            const upToVal = r.upTo.trim().toLowerCase();
            const upTo: number | 'inf' = upToVal === 'inf' || upToVal === 'unlimited' ? 'inf' : parseInt(r.upTo, 10);
            const unitAmountCents =
              r.unitAmount !== '' && !isNaN(Number(r.unitAmount)) && Number(r.unitAmount) >= 0
                ? Math.round(Number(r.unitAmount) * 100)
                : undefined;
            const flatAmountCents =
              r.flatAmount !== '' && !isNaN(Number(r.flatAmount)) && Number(r.flatAmount) >= 0
                ? Math.round(Number(r.flatAmount) * 100)
                : undefined;
            return {
              upTo: upTo === 'inf' || !isNaN(upTo) ? upTo : ('inf' as const),
              ...(unitAmountCents != null && { unitAmount: unitAmountCents }),
              ...(flatAmountCents != null && { flatAmount: flatAmountCents })
            };
          })
      : undefined;

    const entryData = {
      pricebookId,
      productId: formData.productId,
      unitPrice: isTiered && tiersPayload && tiersPayload.length > 0 && tiersPayload[0].unitAmount != null
        ? tiersPayload[0].unitAmount / 100
        : Number(formData.unitPrice),
      currency: formData.currency,
      isActive: formData.isActive,
      effectiveDate: formData.effectiveDate || undefined,
      expirationDate: formData.expirationDate || undefined,
      ...(formData.priceType === 'recurring' && {
        recurring: {
          interval: formData.recurringInterval,
          intervalCount: intervalCountNum != null && !isNaN(intervalCountNum) && intervalCountNum >= 1 ? intervalCountNum : undefined
        } as { interval: 'day' | 'week' | 'month' | 'year'; intervalCount?: number }
      }),
      ...(isTiered && tiersPayload && tiersPayload.length > 0 && {
        billingScheme: 'tiered' as const,
        tiersMode: formData.tiersMode,
        tiers: tiersPayload
      })
    };

    if (isEditMode && entry) {
      updateEntry.mutate(
        { id: entry.id, updates: entryData },
        {
          onSuccess: () => {
            success('Entry updated', 'Pricebook entry has been updated');
            onClose();
          },
          onError: () => {
            showError('Error', 'Failed to update entry');
          }
        }
      );
    } else {
      addEntry.mutate(entryData, {
        onSuccess: () => {
          success('Entry added', 'Product has been added to pricebook');
          onClose();
        },
        onError: () => {
          showError('Error', 'Failed to add entry');
        }
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose}></div>

        <div className="inline-block align-bottom bg-white dark:bg-[#1e293b] rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              {isEditMode ? 'Edit Pricebook Entry' : 'Add Product to Pricebook'}
            </h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300">
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Product <span className="text-red-500">*</span>
                </label>
                <select
                  name="productId"
                  value={formData.productId}
                  onChange={handleChange}
                  disabled={isEditMode}
                  className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-[#334155] text-gray-900 dark:text-white ${
                    errors.productId ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                  } ${isEditMode ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <option value="">Select a product</option>
                  {products?.map(product => (
                    <option key={product.id} value={product.id}>
                      {product.name} ({product.productCode}) - {new Intl.NumberFormat('en-US', { style: 'currency', currency: product.currency }).format(product.unitPrice)}
                    </option>
                  ))}
                </select>
                {errors.productId && <p className="mt-1 text-sm text-red-500">{errors.productId}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Pricing model
                </label>
                <div className="flex gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="pricingModel"
                      value="per_unit"
                      checked={formData.pricingModel === 'per_unit'}
                      onChange={handleChange}
                      className="rounded border-gray-300 dark:border-gray-600"
                    />
                    <span className="text-gray-700 dark:text-gray-300">Per unit</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="pricingModel"
                      value="tiered"
                      checked={formData.pricingModel === 'tiered'}
                      onChange={handleChange}
                      className="rounded border-gray-300 dark:border-gray-600"
                    />
                    <span className="text-gray-700 dark:text-gray-300">Tiered</span>
                  </label>
                </div>
              </div>

              {formData.pricingModel === 'per_unit' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Unit Price <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">
                        {formData.currency === 'USD' ? '$' : formData.currency}
                      </span>
                      <input
                        type="number"
                        name="unitPrice"
                        value={formData.unitPrice}
                        onChange={handleChange}
                        step="0.01"
                        min="0"
                        className={`w-full pl-8 pr-3 py-2 border rounded-lg bg-white dark:bg-[#334155] text-gray-900 dark:text-white ${
                          errors.unitPrice ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                        }`}
                      />
                    </div>
                    {errors.unitPrice && <p className="mt-1 text-sm text-red-500">{errors.unitPrice}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Currency
                    </label>
                    <select
                      name="currency"
                      value={formData.currency}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#334155] text-gray-900 dark:text-white"
                    >
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                      <option value="GBP">GBP</option>
                      <option value="CAD">CAD</option>
                    </select>
                  </div>
                </div>
              )}

              {formData.pricingModel === 'tiered' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Tiers mode
                      </label>
                      <select
                        name="tiersMode"
                        value={formData.tiersMode}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#334155] text-gray-900 dark:text-white"
                      >
                        <option value="graduated">Graduated</option>
                        <option value="volume">Volume</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Currency
                      </label>
                      <select
                        name="currency"
                        value={formData.currency}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#334155] text-gray-900 dark:text-white"
                      >
                        <option value="USD">USD</option>
                        <option value="EUR">EUR</option>
                        <option value="GBP">GBP</option>
                        <option value="CAD">CAD</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Tiers <span className="text-red-500">*</span>
                      </label>
                      <button
                        type="button"
                        onClick={addTierRow}
                        className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline"
                      >
                        Add tier
                      </button>
                    </div>
                    {errors.tiers && <p className="mb-2 text-sm text-red-500">{errors.tiers}</p>}
                    <div className="space-y-3">
                      {tierRows.map((row, index) => (
                        <div
                          key={index}
                          className="grid grid-cols-12 gap-2 items-start p-2 rounded border border-gray-200 dark:border-gray-600"
                        >
                          <div className="col-span-3">
                            <input
                              type="text"
                              placeholder="Up to or Unlimited"
                              value={row.upTo}
                              onChange={e => updateTierRow(index, 'upTo', e.target.value)}
                              className={`w-full px-2 py-1.5 text-sm border rounded bg-white dark:bg-[#334155] text-gray-900 dark:text-white ${
                                errors[`tier_${index}`] ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                              }`}
                            />
                          </div>
                          <div className="col-span-3">
                            <div className="relative">
                              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                              <input
                                type="text"
                                placeholder="Unit amt"
                                value={row.unitAmount}
                                onChange={e => updateTierRow(index, 'unitAmount', e.target.value)}
                                className="w-full pl-5 pr-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-[#334155] text-gray-900 dark:text-white"
                              />
                            </div>
                          </div>
                          <div className="col-span-3">
                            <div className="relative">
                              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                              <input
                                type="text"
                                placeholder="Flat (opt)"
                                value={row.flatAmount}
                                onChange={e => updateTierRow(index, 'flatAmount', e.target.value)}
                                className="w-full pl-5 pr-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-[#334155] text-gray-900 dark:text-white"
                              />
                            </div>
                          </div>
                          <div className="col-span-2 flex items-center h-[34px]">
                            <button
                              type="button"
                              onClick={() => removeTierRow(index)}
                              disabled={tierRows.length <= 1}
                              className="text-sm text-red-600 dark:text-red-400 hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Remove
                            </button>
                          </div>
                          {errors[`tier_${index}`] && (
                            <p className="col-span-12 text-sm text-red-500">{errors[`tier_${index}`]}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Billing
                </label>
                <div className="flex gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="priceType"
                      value="one-time"
                      checked={formData.priceType === 'one-time'}
                      onChange={handleChange}
                      className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">One-time</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="priceType"
                      value="recurring"
                      checked={formData.priceType === 'recurring'}
                      onChange={handleChange}
                      className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Recurring</span>
                  </label>
                </div>
              </div>

              {formData.priceType === 'recurring' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Billing interval
                    </label>
                    <select
                      name="recurringInterval"
                      value={formData.recurringInterval}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#334155] text-gray-900 dark:text-white"
                    >
                      <option value="day">Day</option>
                      <option value="week">Week</option>
                      <option value="month">Month</option>
                      <option value="year">Year</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Every (e.g. 1 month, 3 months)
                    </label>
                    <input
                      type="number"
                      name="recurringIntervalCount"
                      value={formData.recurringIntervalCount}
                      onChange={handleChange}
                      min={1}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#334155] text-gray-900 dark:text-white"
                    />
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Effective Date
                  </label>
                  <input
                    type="date"
                    name="effectiveDate"
                    value={formData.effectiveDate}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#334155] text-gray-900 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Expiration Date
                  </label>
                  <input
                    type="date"
                    name="expirationDate"
                    value={formData.expirationDate}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#334155] text-gray-900 dark:text-white"
                  />
                </div>
              </div>

              <div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleChange}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Active</span>
                </label>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-[#334155] border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-[#475569] transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={addEntry.isPending || updateEntry.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {addEntry.isPending || updateEntry.isPending
                  ? 'Saving...'
                  : isEditMode
                  ? 'Update Entry'
                  : 'Add Product'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
